/**
 * Editable patient-tag cell for the notification table.
 */

import { FormEvent, MouseEvent, useState } from 'react';
import type { Patient } from '../../../../types';

interface PatientTagsCellProps {
  patient: Patient;
  onPatientUpdate?: (patient: Patient) => void;
}

interface MenuPosition {
  left: number;
  top: number;
}

/**
 * Render removable tag pills and a custom-tag add control.
 *
 * @param props - Patient row and optional update callback.
 * @returns The editable patient-tags cell.
 */
export function PatientTagsCell({ patient, onPatientUpdate }: PatientTagsCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customTag, setCustomTag] = useState('');
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const tags = patient.patientTags ?? [];

  /**
   * Send a de-duplicated tag list to the table state owner.
   *
   * @param nextTags - Replacement tags for this patient.
   * @returns Nothing.
   */
  const updateTags = (nextTags: string[]) => {
    onPatientUpdate?.({ ...patient, patientTags: Array.from(new Set(nextTags)) });
  };

  /**
   * Add a trimmed custom tag from the popover form.
   *
   * @param event - Custom-tag form submission.
   * @returns Nothing.
   */
  const addCustomTag = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const tag = customTag.trim();
    if (!tag) return;
    updateTags([...tags, tag]);
    setCustomTag('');
  };

  /**
   * Open or close the fixed-position tag editor.
   *
   * @param event - Add-tag button click.
   * @returns Nothing.
   */
  const toggleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isOpen) {
      setIsOpen(false);
      setMenuPosition(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 280;
    const left = Math.min(rect.left, window.innerWidth - menuWidth - 16);
    setMenuPosition({ left: Math.max(16, left), top: rect.bottom + 8 });
    setIsOpen(true);
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-1" onClick={(event) => event.stopPropagation()}>
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-1.5 py-0.5 text-xs font-medium text-[#1E40AF]"
        >
          <span className="truncate" title={tag}>
            {tag}
          </span>
          <button
            type="button"
            className="leading-none hover:text-blue-900 focus:outline-none"
            onClick={() => updateTags(tags.filter((existingTag) => existingTag !== tag))}
            aria-label={`Remove patient tag ${tag}`}
          >
            ×
          </button>
        </span>
      ))}

      <button
        type="button"
        className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-dashed border-blue-300 text-xs font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={toggleMenu}
        aria-label="Add patient tag"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        +
      </button>

      {isOpen && menuPosition && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            onClick={(event) => {
              event.stopPropagation();
              setIsOpen(false);
              setMenuPosition(null);
            }}
            aria-label="Close patient tag editor"
          />
          <div
            role="dialog"
            aria-label="Patient tag editor"
            className="fixed z-50 w-[280px] rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
            style={{ left: menuPosition.left, top: menuPosition.top }}
            onClick={(event) => event.stopPropagation()}
          >
            <form onSubmit={addCustomTag}>
              <label htmlFor={`custom-tag-${patient.id}`} className="mb-1 block text-xs font-semibold text-gray-700">
                Custom tag
              </label>
              <div className="flex gap-2">
                <input
                  id={`custom-tag-${patient.id}`}
                  value={customTag}
                  onChange={(event) => setCustomTag(event.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none"
                  placeholder="e.g. Follow-up"
                  maxLength={40}
                />
                <button
                  type="submit"
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  disabled={!customTag.trim()}
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

export default PatientTagsCell;
